import type { ExploraPayload, ExploraSkin, ExploraVariable } from '../../core/types/payloads/explora.types';
import { defaultInverseDemo, defaultInverseOperations } from './balanzaEquation';

export interface ExploraSkinPreset {
  id: ExploraSkin;
  label: string;
  icon: string;
  unitLabel: string;
  displayEquation?: string;
  variables: ExploraVariable[];
  deckMode?: boolean;
}

export const EXPLORA_SKIN_PRESETS: Record<ExploraSkin, ExploraSkinPreset> = {
  physics: {
    id: 'physics',
    label: 'Física / Pesos',
    icon: '⚖️',
    unitLabel: 'kg',
    variables: [
      { id: 'add1', label: '+ 1 kg', internalValue: 1, type: 'ADD' },
      { id: 'add5', label: '+ 5 kg', internalValue: 5, type: 'ADD' },
      { id: 'sub1', label: '− 1 kg', internalValue: -1, type: 'ADD' },
      { id: 'sub3', label: '− 3 kg', internalValue: -3, type: 'ADD' },
    ],
  },
  algebra: {
    id: 'algebra',
    label: 'Álgebra',
    icon: '📐',
    unitLabel: 'kg',
    displayEquation: '2x + 4 = 10',
    variables: defaultInverseOperations(),
  },
  economy: {
    id: 'economy',
    label: 'Economía',
    icon: '📈',
    unitLabel: '%',
    deckMode: true,
    variables: [
      { id: 'double', label: 'Duplicar (×2)', internalValue: 2, type: 'MULTIPLY' },
      { id: 'halve', label: 'Reducir a la mitad', internalValue: 0.5, type: 'MULTIPLY' },
      { id: 'up33', label: '+33%', internalValue: 1.33, type: 'MULTIPLY' },
      { id: 'down10', label: '−10%', internalValue: 0.9, type: 'MULTIPLY' },
    ],
  },
  fractions: {
    id: 'fractions',
    label: 'Fracciones',
    icon: '🍕',
    unitLabel: '',
    deckMode: true,
    variables: [
      { id: 'eighth', label: 'Añadir ⅛', internalValue: 1, type: 'ADD' },
      { id: 'quarter', label: 'Añadir ¼', internalValue: 2, type: 'ADD' },
      { id: 'double', label: 'Duplicar', internalValue: 2, type: 'MULTIPLY' },
      { id: 'halve', label: 'Reducir a la mitad', internalValue: 0.5, type: 'MULTIPLY' },
    ],
  },
};

export function applyExploraSkin(payload: ExploraPayload, skin: ExploraSkin): ExploraPayload {
  const preset = EXPLORA_SKIN_PRESETS[skin];
  const variant =
    skin === 'fractions' ? 'fracciones' : skin === 'economy' ? 'grafico' : 'balanza';

  if (skin === 'algebra') {
    const { equationLeft, equationRight, displayEquation } = defaultInverseDemo();
    return {
      ...payload,
      variant,
      skin,
      unitLabel: preset.unitLabel,
      displayEquation: payload.displayEquation ?? displayEquation,
      balanceMode: 'inverse-operations',
      equationLeft: payload.equationLeft ?? equationLeft,
      equationRight: payload.equationRight ?? equationRight,
      targetValue: payload.equationRight?.constants ?? equationRight.constants,
      initialValue: payload.equationRight?.constants ?? equationRight.constants,
      deckMode: false,
      availableVariables: preset.variables.map((v, i) => ({
        ...v,
        id: `${skin}_${v.id}_${i}`,
      })),
    };
  }

  return {
    ...payload,
    variant,
    skin,
    unitLabel: preset.unitLabel,
    displayEquation: preset.displayEquation ?? payload.displayEquation,
    deckMode: preset.deckMode ?? payload.deckMode,
    availableVariables: preset.variables.map((v, i) => ({
      ...v,
      id: `${skin}_${v.id}_${i}`,
    })),
  };
}

export function resolveSkin(payload: ExploraPayload): ExploraSkin {
  if (payload.skin) return payload.skin;
  if (payload.displayEquation) return 'algebra';
  if (payload.variant === 'fracciones') return 'fractions';
  if (payload.variant === 'grafico') return 'economy';
  return 'physics';
}
